import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  PhoneOutgoing,
  PhoneIncoming,
  FileText,
  Users,
  BarChart3,
  Scale,
  Shield,
  DollarSign,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Zap,
  Star,
} from "lucide-react";

const Home = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isVisible, setIsVisible] = useState({});
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const observerRef = useRef(null);
  const heroRef = useRef(null);

  // Mouse tracking for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const services = [
    {
      icon: PhoneOutgoing,
      title: "Outbound Services",
      description:
        "Proactive customer outreach, lead generation, telemarketing, and sales campaigns.",
    },
    {
      icon: PhoneIncoming,
      title: "Inbound Services",
      description:
        "Customer support, order processing, helpdesk, and inquiry handling.",
    },
    {
      icon: FileText,
      title: "Back Office Support",
      description:
        "Data entry, document processing, accounting support, and administrative tasks.",
    },
  ];

  const whyChooseUs = [
    {
      icon: Users,
      title: "Skilled & Trained Agents",
      description: "Professionally trained workforce delivering excellence.",
    },
    {
      icon: Shield,
      title: "Quality Monitoring",
      description: "Continuous QA processes ensuring top performance.",
    },
    {
      icon: Scale,
      title: "Flexible Scaling",
      description: "Easily scale operations based on your needs.",
    },
    {
      icon: BarChart3,
      title: "Transparent Reporting",
      description: "Real-time analytics and detailed performance reports.",
    },
    {
      icon: DollarSign,
      title: "Cost-Effective Solutions",
      description: "Premium services at competitive pricing.",
    },
  ];

  const galleryImages = [
    {
      url: "/images/image-14.jpg",
      alt: "NUVEXA Call Center Floor",
    },
    {
      url: "/images/image-5.jpg",
      alt: "NUVEXA Executive Office",
    },
    {
      url: "/images/image-7.jpg",
      alt: "NUVEXA HR Office",
    },
    {
      url: "/images/image-3.jpg",
      alt: "NUVEXA Lounge Area",
    },
  ];

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".reveal").forEach((el) => {
      observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="min-h-screen flex items-center relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/bg-dark.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f0f10]/90 via-[#0f0f10]/70 to-transparent" />
        
        {/* Mouse spotlight effect */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full pointer-events-none opacity-20 transition-opacity duration-300"
          style={{
            background: 'radial-gradient(circle, rgba(109, 180, 50, 0.3) 0%, transparent 70%)',
            left: mousePosition.x - 300,
            top: mousePosition.y - 300,
          }}
        />

        {/* Floating decorative elements */}
        <div className="absolute top-20 right-20 animate-float opacity-20">
          <Sparkles className="w-8 h-8 text-[#6DB432]" />
        </div>
        <div className="absolute bottom-40 right-40 animate-float delay-500 opacity-20">
          <Zap className="w-6 h-6 text-[#8BC064]" />
        </div>
        <div className="absolute top-40 left-[60%] animate-float delay-300 opacity-15">
          <Star className="w-5 h-5 text-[#6DB432]" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl stagger-children">
            {/* Animated pulse badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#6DB432]/10 border border-[#6DB432]/30 mb-6 pulse-badge backdrop-blur-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-[#6DB432] animate-pulse" />
              <span className="text-[#6DB432] text-sm font-semibold tracking-wide">Leading BPO Solutions</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              <span className="inline-block hover:scale-105 transition-transform duration-300">Smart</span>{" "}
              <span className="inline-block hover:scale-105 transition-transform duration-300">Outsourcing.</span>
              <br />
              <span className="real-results-text relative inline-block">
                Real Results.
              </span>
            </h1>
            <p className="mt-4 text-[#6DB432] text-xl sm:text-2xl font-bold tracking-widest uppercase">
              <span className="glitch" data-text="Powered by PRECISION">Powered by PRECISION</span>
            </p>
            <p className="mt-6 text-gray-300 text-lg sm:text-xl leading-relaxed max-w-2xl">
              NUVEXA delivers reliable inbound, outbound, and back-office
              solutions designed to help businesses grow efficiently.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  data-testid="hero-get-started-btn"
                  className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-8 py-6 text-lg rounded-xl btn-glow btn-shine ripple group rubberBand-hover shadow-lg shadow-[#6DB432]/20"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  data-testid="hero-contact-btn"
                  variant="outline"
                  className="border-2 border-[#6DB432] text-white font-medium px-8 py-6 text-lg rounded-xl bg-transparent transition-all duration-300 wobble-hover hover:bg-[#6DB432] [&:hover]:text-white backdrop-blur-sm"
                >
                  Contact Us
                </Button>
              </Link>
            </div>

            {/* Glassmorphism Stats */}
            <div className="mt-16 flex gap-8 flex-wrap justify-start">
              <div className="glass-card px-8 py-6 rounded-2xl text-center group cursor-default floating-stats">
                <div className="relative">
                  <div className="text-5xl md:text-6xl font-extrabold text-[#6DB432] stats-glow group-hover:scale-110 transition-transform duration-300">
                    4
                  </div>
                  <div className="absolute -inset-4 bg-[#6DB432]/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <div className="text-gray-300 text-sm mt-3 uppercase tracking-widest group-hover:text-[#6DB432] transition-colors duration-300 font-medium">Campaigns</div>
              </div>
              <div className="hidden sm:block w-px h-20 bg-gradient-to-b from-transparent via-[#6DB432]/40 to-transparent" />
              <div className="glass-card px-8 py-6 rounded-2xl text-center group cursor-default floating-stats" style={{ animationDelay: '0.5s' }}>
                <div className="relative">
                  <div className="text-5xl md:text-6xl font-extrabold text-[#6DB432] stats-glow group-hover:scale-110 transition-transform duration-300">
                    40<span className="text-[#8BC064]">+</span>
                  </div>
                  <div className="absolute -inset-4 bg-[#6DB432]/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                <div className="text-gray-300 text-sm mt-3 uppercase tracking-widest group-hover:text-[#6DB432] transition-colors duration-300 font-medium">Agents</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <ChevronDown className="w-8 h-8 text-[#6DB432]" />
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0f0f10] to-transparent z-20" />
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-[#0f0f10] relative overflow-hidden">
        {/* Decorative X mark */}
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -right-20 top-1/2 -translate-y-1/2 h-[500px] w-auto opacity-[0.12] pointer-events-none object-contain floating-x"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative reveal">
              {/* Animated funky frame container */}
              <div className="relative group">
                {/* Animated outer glow */}
                <div className="absolute -inset-6 bg-gradient-to-br from-[#6DB432]/30 via-transparent to-[#8BC064]/20 rounded-3xl blur-2xl opacity-50 group-hover:opacity-100 transition-all duration-700 animate-pulse" style={{ animationDuration: '3s' }} />
                
                {/* Rotating border effect */}
                <div className="absolute -inset-2 rounded-2xl opacity-30 group-hover:opacity-60 transition-opacity duration-500" style={{
                  background: 'conic-gradient(from 0deg, #6DB432, transparent, #8BC064, transparent, #6DB432)',
                  animation: 'spin 8s linear infinite'
                }} />
                
                {/* Main image container */}
                <div className="relative bg-[#0f0f10] p-1 rounded-2xl">
                  {/* Corner accent shapes */}
                  <div className="absolute -top-4 -left-4 w-16 h-16 border-t-4 border-l-4 border-[#6DB432] rounded-tl-3xl z-20 group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute -bottom-4 -right-4 w-16 h-16 border-b-4 border-r-4 border-[#6DB432] rounded-br-3xl z-20 group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute -top-2 -right-2 w-10 h-10 border-t-2 border-r-2 border-[#8BC064]/60 rounded-tr-xl z-20" />
                  <div className="absolute -bottom-2 -left-2 w-10 h-10 border-b-2 border-l-2 border-[#8BC064]/60 rounded-bl-xl z-20" />
                  
                  {/* Image with mask for faded edges */}
                  <div className="relative overflow-hidden rounded-xl" style={{
                    maskImage: 'radial-gradient(ellipse 95% 95% at 50% 50%, black 70%, transparent 100%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 95% 95% at 50% 50%, black 70%, transparent 100%)'
                  }}>
                    <img
                      src="/images/girl.png"
                      alt="NUVEXA Call Center Team"
                      className="w-full h-auto object-contain brightness-100 group-hover:brightness-110 group-hover:scale-105 transition-all duration-700"
                    />
                    {/* Shimmer effect overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
                  </div>
                  
                  {/* Gradient fade overlays for blending */}
                  <div className="absolute inset-0 rounded-xl pointer-events-none">
                    <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0f0f10] to-transparent opacity-50" />
                    <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-[#0f0f10] to-transparent opacity-30" />
                  </div>
                </div>
                
                {/* Floating decorative elements */}
                <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-[#6DB432]/5 rounded-full blur-3xl" />
                <div className="absolute -top-6 -left-6 w-20 h-20 border border-[#6DB432]/30 rounded-full animate-ping opacity-20" style={{ animationDuration: '3s' }} />
                <div className="absolute top-1/2 -left-6 w-3 h-20 bg-gradient-to-b from-transparent via-[#6DB432]/60 to-transparent rounded-full group-hover:h-28 transition-all duration-500" />
                <div className="absolute bottom-1/3 -right-6 w-3 h-16 bg-gradient-to-b from-transparent via-[#8BC064]/50 to-transparent rounded-full group-hover:h-24 transition-all duration-500" />
                
                {/* Animated dots */}
                <div className="absolute -top-2 left-1/4 w-2 h-2 bg-[#6DB432] rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                <div className="absolute -bottom-2 right-1/3 w-2 h-2 bg-[#8BC064] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }} />
              </div>
            </div>
            <div className="space-y-6 reveal">
              <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest animate-fade-in-left">
                About NUVEXA
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                Your Trusted Partner in
                <br />
                <span className="gradient-text-animated">Business Growth</span>
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed">
                NUVEXA LLC is a premier BPO and call center company dedicated to
                delivering high-quality outsourcing solutions. With a team of
                skilled professionals and state-of-the-art infrastructure, we
                help businesses streamline operations and achieve their goals.
              </p>
              <Link to="/about">
                <Button
                  data-testid="about-learn-more-btn"
                  className="bg-transparent border border-[#6DB432] text-[#6DB432] hover:bg-[#6DB432] hover:text-white font-bold px-6 py-3 rounded-md mt-4 btn-shine group"
                >
                  Learn More
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 bg-[#353537]/30 relative">
        {/* Animated shapes */}
        <div className="absolute top-10 left-10 w-20 h-20 border border-[#6DB432]/20 rounded-lg animate-float opacity-30" style={{ animationDuration: '8s' }} />
        <div className="absolute bottom-20 right-20 w-16 h-16 border border-[#8BC064]/20 rounded-full animate-float opacity-30" style={{ animationDuration: '6s', animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <Zap className="w-4 h-4 animate-pulse" />
              What We Offer
              <Zap className="w-4 h-4 animate-pulse" />
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">
              Our Core <span className="gradient-text-animated">Services</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-testid={`service-card-${index}`}
                className="reveal tilt-card gradient-border p-8 rounded-xl group relative overflow-hidden pop-out spotlight-sweep float-card"
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                {/* Hover background overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#6DB432] to-[#8BC064] opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#6DB432]/30 to-[#8BC064]/10 group-hover:from-white/20 group-hover:to-white/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 border border-[#6DB432]/30 group-hover:border-white/30 icon-spin">
                    <service.icon className="w-8 h-8 text-[#6DB432] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-white group-hover:text-[#1a1a1b] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed mt-3 group-hover:text-[#1a1a1b]/80 transition-colors duration-300">
                    {service.description}
                  </p>
                  {/* Hover indicator */}
                  <div className="mt-6 flex items-center text-[#6DB432] group-hover:text-[#1a1a1b] opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <span className="text-sm font-semibold">Explore Service</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 reveal">
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-8 py-3 rounded-xl btn-glow btn-shine group elastic-scale particle-burst"
              >
                View All Services
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Inside NUVEXA - Gallery */}
      <section className="py-24 bg-[#0f0f10] relative">
        {/* Decorative corner elements */}
        <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-[#6DB432]/20 rounded-tl-3xl" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-[#6DB432]/20 rounded-br-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2">
              <Star className="w-4 h-4 animate-spin" style={{ animationDuration: '3s' }} />
              Inside NUVEXA
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">
              Inside Our <span className="gradient-text-animated">Operations</span>
            </h2>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto">
              A glimpse into our professional and productive environment.
            </p>
          </div>

          <div className="bento-grid">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                data-testid={`gallery-image-${index}`}
                className="bento-item reveal cursor-pointer"
                onClick={() => setSelectedImage(image)}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="parallax-img"
                />
                <div className="bento-overlay">
                  <div className="text-white">
                    <p className="font-bold text-lg">{image.alt}</p>
                    <p className="text-[#6DB432] text-sm mt-1 flex items-center gap-1">
                      <span>View</span>
                      <ArrowRight className="w-3 h-3" />
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-[#353537] border-[#6DB432]/20 p-2 animate-scale-in">
          {selectedImage && (
            <img
              src={selectedImage.url}
              alt={selectedImage.alt}
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Why Choose NUVEXA */}
      <section className="py-24 bg-[#353537]/30 relative overflow-hidden">
        {/* Decorative X mark */}
        <img
          src="/images/x-mark.png"
          alt=""
          className="absolute -left-16 bottom-10 h-[350px] w-auto opacity-[0.1] pointer-events-none object-contain floating-x"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 reveal">
            <span className="text-[#6DB432] font-bold text-sm uppercase tracking-widest">
              Why Us
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-white">
              Why Choose NUVEXA
            </h2>
          </div>

          {/* Grid layout - stacks on mobile, 5 columns on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={item.title}
                data-testid={`why-card-${index}`}
                className="reveal glass-card text-center p-6 rounded-2xl group floating-stats"
                style={{ 
                  transitionDelay: `${index * 0.1}s`,
                  animationDelay: `${index * 0.5}s`
                }}
              >
                <div className="icon-circle-loader w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#6DB432]/30 to-[#8BC064]/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-all duration-500">
                  <item.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#6DB432]" />
                </div>
                <h3 className="text-white font-bold mb-2 text-sm sm:text-base group-hover:text-[#6DB432] transition-colors duration-300">{item.title}</h3>
                <p className="text-gray-400 text-xs sm:text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 relative overflow-hidden"
        style={{
          backgroundImage: `url('/images/entrance.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#353537]/95 to-[#353537]/80" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center reveal">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 bouncy-entrance">
              Let's Build Growth
            </h2>
            <p className="gradient-text-animate text-xl sm:text-2xl font-bold tracking-widest uppercase mb-8">
              Powered by PRECISION
            </p>
            <Link to="/contact">
              <Button
                data-testid="cta-get-started-btn"
                className="bg-[#6DB432] hover:bg-[#8BC064] text-white font-bold px-12 py-6 text-lg rounded-xl heartbeat btn-shine elastic-scale"
              >
                Get Started Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
